import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { EmailService } from '../services/email.service';

export interface TherapistContactFormData {
  name: string;
  email: string;
  phone: string;
  reason: string;
  therapistName: string;
  therapistId: string;
}

export const useTherapistContactForm = (therapistName: string, therapistId: string) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<TherapistContactFormData>();

  // Validaciones robustas
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email es opcional
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && !email.includes('..') && !/^[.-]/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const cleaned = phone.replace(/[\s\-()]/g, '');
    const withoutCountryCode = cleaned.replace(/^\+?56/, '');
    return /^[2-9]\d{7,8}$/.test(withoutCountryCode);
  };

  const validateName = (name: string): boolean => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 50) return false;
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(trimmed)) return false;
    const words = trimmed.split(/\s+/);
    return words.length >= 2 && words.every(word => word.length > 0);
  };

  const validateReason = (reason: string): boolean => {
    const trimmed = reason.trim();
    if (trimmed.length < 10 || trimmed.length > 500) return false;
    const words = trimmed.split(/\s+/).filter(word => word.length > 0);
    return words.length >= 3;
  };

  // Mostrar mensaje de estado
  const showMessage = useCallback((type: 'success' | 'error', message: string) => {
    setSubmitStatus({ type, message });
    
    // Auto-ocultar mensaje después de 5 segundos
    setTimeout(() => {
      setSubmitStatus({ type: null, message: '' });
    }, 5000);
  }, []);

  // Función para enviar el formulario
  const onSubmit = useCallback(async (data: TherapistContactFormData) => {
    if (isSubmitting) return;

    // Validaciones adicionales robustas
    if (!validateName(data.name)) {
      showMessage('error', 'Por favor, ingresa un nombre completo válido (nombre y apellido)');
      return;
    }

    if (data.email && !validateEmail(data.email)) {
      showMessage('error', 'Por favor, ingresa un correo electrónico válido');
      return;
    }

    if (!validatePhone(data.phone)) {
      showMessage('error', 'Por favor, ingresa un número chileno válido (ej: 912345678)');
      return;
    }

    if (!validateReason(data.reason)) {
      showMessage('error', 'Por favor, describe el motivo de tu consulta con al menos 10 caracteres');
      return;
    }

    setIsSubmitting(true);

    try {
      await EmailService.sendContactForm({
        name:          data.name,
        email:         data.email,
        phone:         data.phone,
        reason:        data.reason,
        origin:        `Página del terapeuta: ${therapistName}`,
        therapistName,
      });

      showMessage('success', `¡Mensaje enviado con éxito! ${therapistName} se pondrá en contacto contigo pronto.`);
      
      // Limpiar formulario
      reset();
      
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      showMessage('error', 'Hubo un problema al enviar el mensaje. Intenta nuevamente o contáctanos por WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, showMessage, reset, therapistName]);

  // Limpiar mensaje de estado
  const clearMessage = useCallback(() => {
    setSubmitStatus({ type: null, message: '' });
  }, []);

  return {
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,
    submitStatus,
    clearMessage,
    validateEmail
  };
};